import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Avatar,
  Rating,
  IconButton,
  useTheme as useMuiTheme,
  useMediaQuery
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import SwipeRoundedIcon from '@mui/icons-material/SwipeRounded';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';

const defaultTestimonials = [
  {
    id: 't1',
    name: {
      en: 'David Johnson',
      ge: 'დავით ჯონსონი',
      ru: 'Дэвид Джонсон'
    },
    position: {
      en: 'CEO, TechCorp',
      ge: 'აღმასრულებელი დირექტორი, TechCorp',
      ru: 'Генеральный директор, TechCorp'
    },
    avatar: '👨‍💼',
    rating: 5,
    text: {
      en: 'ITFORSEC has been an invaluable partner for our business. Their IT support services have significantly improved our operational efficiency and security posture.',
      ge: 'ITFORSEC იყო შეუფასებელი პარტნიორი ჩვენი ბიზნესისთვის. მათმა IT მხარდაჭერის სერვისებმა მნიშვნელოვნად გააუმჯობესა ჩვენი საოპერაციო ეფექტურობა და უსაფრთხოება.',
      ru: 'ITFORSEC стал неоценимым партнером для нашего бизнеса. Их услуги IT-поддержки значительно повысили нашу операционную эффективность и безопасность.'
    },
    enabled: true
  },
  {
    id: 't2',
    name: {
      en: 'Sarah Williams',
      ge: 'სარა უილიამსი',
      ru: 'Сара Уильямс'
    },
    position: {
      en: 'CTO, InnovateTech',
      ge: 'ტექნიკური დირექტორი, InnovateTech',
      ru: 'Технический директор, InnovateTech'
    },
    avatar: '👩‍💼',
    rating: 5,
    text: {
      en: 'The team at ITFORSEC delivered exceptional web development services for our company. Their attention to detail and technical expertise exceeded our expectations.',
      ge: 'ITFORSEC-ის გუნდმა ჩვენი კომპანიისთვის უმაღლესი დონის ვებ-დეველოპმენტის სერვისები შეასრულა. დეტალებისადმი ყურადღებამ და ტექნიკურმა გამოცდილებამ მოლოდინს გადააჭარბა.',
      ru: 'Команда ITFORSEC предоставила исключительные услуги веб-разработки для нашей компании. Внимание к деталям и технический опыт превзошли наши ожидания.'
    },
    enabled: true
  },
  {
    id: 't3',
    name: {
      en: 'Michael Brown',
      ge: 'მიხეილ ბრაუნი',
      ru: 'Майкл Браун'
    },
    position: {
      en: 'Operations Manager, GlobalSoft',
      ge: 'საოპერაციო მენეჯერი, GlobalSoft',
      ru: 'Операционный менеджер, GlobalSoft'
    },
    avatar: '👨‍💻',
    rating: 5,
    text: {
      en: "We've been working with ITFORSEC for over a year now, and their network security solutions have been rock-solid. Highly recommended for businesses of all sizes.",
      ge: 'ჩვენ უკვე ერთ წელზე მეტია ვთანამშრომლობთ ITFORSEC-თან და მათი ქსელის უსაფრთხოების გადაწყვეტილებები შეუცვლელია. რეკომენდებულია ნებისმიერი ზომის ბიზნესისთვის.',
      ru: 'Мы работаем с ITFORSEC уже больше года, и их решения по сетевой безопасности безупречны. Очень рекомендуем для бизнеса любого масштаба.'
    },
    enabled: true
  },
  {
    id: 't4',
    name: {
      en: 'Elena Rostova',
      ge: 'ელენა როსტოვა',
      ru: 'Елена Ростова'
    },
    position: {
      en: 'Product Director, CyberVault',
      ge: 'პროდუქტის დირექტორი, CyberVault',
      ru: 'Директор по продукту, CyberVault'
    },
    avatar: '👩‍💻',
    rating: 5,
    text: {
      en: 'The security audit conducted by ITFORSEC uncovered critical vulnerabilities before our product launch. Their remediation advice was exceptionally rapid and precise.',
      ge: 'ITFORSEC-ის მიერ ჩატარებულმა უსაფრთხოების აუდიტმა პროდუქტის გაშვებამდე კრიტიკული ხარვეზები გამოავლინა. მათი რეკომენდაციები იყო უზადოდ ზუსტი.',
      ru: 'Аудит безопасности, проведенный ITFORSEC, выявил критические уязвимости до запуска нашего продукта. Рекомендации по исправлению были быстрыми и точными.'
    },
    enabled: true
  },
  {
    id: 't5',
    name: {
      en: 'Giorgi Beridze',
      ge: 'გიორგი ბერიძე',
      ru: 'Георгий Беридзе'
    },
    position: {
      en: 'Co-Founder, Fintech Solutions',
      ge: 'თანადამფუძნებელი, Fintech Solutions',
      ru: 'Сооснователь, Fintech Solutions'
    },
    avatar: '👨‍💼',
    rating: 5,
    text: {
      en: 'Outstanding local LLM installation and private AI automation. Our sensitive financial data stays on-premise while our team gains exponential productivity.',
      ge: 'უმაღლესი დონის ლოკალური LLM-ის ინსტალაცია და AI ავტომატიზაცია. ჩვენი ფინანსური მონაცემები დარჩა სრულად დაცული, ხოლო გუნდის პროდუქტიულობა გაიზარდა.',
      ru: 'Выдающаяся настройка локальных LLM и приватная автоматизация AI. Наши финансовые данные остаются изолированными, а продуктивность выросла в разы.'
    },
    enabled: true
  }
];

// Helper: detects if a background string represents a dark color
const isDarkColor = (color) => {
  if (!color || typeof color !== 'string') return true;
  const c = color.trim().toLowerCase();
  if (
    c.includes('18, 18, 18') ||
    c.includes('30, 41, 59') ||
    c.includes('15, 23, 42') ||
    c.includes('10, 14, 23') ||
    c.includes('11, 24, 39') ||
    c.includes('17, 24, 39') ||
    c === '#000' ||
    c === '#000000' ||
    c === '#111827' ||
    c === '#121212' ||
    c === '#1e1e1e' ||
    c === '#1e293b' ||
    c === '#0f172a' ||
    c === '#0a0e17' ||
    c === '#0b0f19'
  ) return true;
  if (c.startsWith('#')) {
    const hex = c.replace('#', '');
    let r = 0, g = 0, b = 0;
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length >= 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) < 128;
  }
  const rgbMatch = c.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) < 128;
  }
  return false;
};

const Testimonials = () => {
  const { isDarkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const { language, t, testimonialsList, testimonialsUi } = useLanguage();

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));

  // Active items
  const activeTestimonials = Array.isArray(testimonialsList) && testimonialsList.length > 0
    ? testimonialsList.filter(t => t.enabled !== false)
    : defaultTestimonials;

  // Custom UI settings from Dashboard (with light mode adaptability)
  const ui = testimonialsUi || {};
  const sectionBg = isDarkMode
    ? (ui.section_bg || 'rgba(18, 18, 18, 0.8)')
    : (ui.section_bg_light || (isDarkColor(ui.section_bg) ? 'rgba(245, 247, 250, 0.85)' : (ui.section_bg || 'rgba(245, 247, 250, 0.85)')));
  const cardBg = isDarkMode
    ? (ui.card_bg || 'rgba(30, 41, 59, 0.7)')
    : (ui.card_bg_light || (isDarkColor(ui.card_bg) ? '#ffffff' : (ui.card_bg || '#ffffff')));
  const cardStyle = ui.card_style || 'cyber-glow';
  const accentColor = ui.accent_color || '#00C896';
  const cardRadius = ui.card_radius || '16px';
  const isCarousel = ui.enable_carousel !== false;
  const isAutoplay = ui.autoplay !== false;
  const autoplaySpeed = parseInt(ui.autoplay_speed, 10) || 5000;
  const showArrows = ui.show_arrows !== false;
  const showDots = ui.show_dots !== false;
  const showRatings = ui.show_ratings !== false;
  const showQuoteIcon = ui.show_quote_icon !== false;

  // Determine items per view
  const itemsPerView = isMobile ? 1 : (isTablet ? 2 : 3);
  const totalItems = activeTestimonials.length;
  const maxIndex = Math.max(0, totalItems - itemsPerView);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  const [isPaused, setIsPaused] = useState(false);

  // Touch & Drag Swipe tracking
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const isSwiping = useRef(false);

  // Keep index within bounds if window resizes
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [itemsPerView, maxIndex, currentIndex]);

  const handleNext = useCallback(() => {
    setDirection(1);
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const handlePrev = useCallback(() => {
    setDirection(-1);
    setCurrentIndex(prev => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  // Autoplay timer
  useEffect(() => {
    if (!isCarousel || !isAutoplay || isPaused || totalItems <= itemsPerView) return;
    const timer = setInterval(() => {
      handleNext();
    }, autoplaySpeed);
    return () => clearInterval(timer);
  }, [isCarousel, isAutoplay, isPaused, autoplaySpeed, totalItems, itemsPerView, handleNext]);

  // Touch Swipe Handlers (Left/Right)
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isSwiping.current = true;
    setIsPaused(true);
  };

  const onTouchMove = (e) => {
    if (!isSwiping.current || touchStartX.current === null) return;
    const diffX = touchStartX.current - e.touches[0].clientX;
    const diffY = touchStartY.current - e.touches[0].clientY;
    // If predominantly horizontal swipe, prevent vertical scroll jitter
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
      if (e.cancelable) e.preventDefault();
    }
  };

  const onTouchEnd = (e) => {
    if (!isSwiping.current || touchStartX.current === null) return;
    const diffX = touchStartX.current - e.changedTouches[0].clientX;
    const minSwipeDistance = 45; // 45px swipe threshold

    if (diffX > minSwipeDistance) {
      handleNext(); // Swiped left -> show next
    } else if (diffX < -minSwipeDistance) {
      handlePrev(); // Swiped right -> show prev
    }

    touchStartX.current = null;
    touchStartY.current = null;
    isSwiping.current = false;
    setTimeout(() => setIsPaused(false), 2000);
  };

  // Helper to extract localized text
  const getLocalized = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val[language] || val.en || Object.values(val)[0] || '';
  };

  // Dynamic card styling presets based on dashboard config and active theme
  const getCardStyleSx = () => {
    const base = {
      p: { xs: 3, md: 3.8 },
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: cardRadius,
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
      backgroundColor: cardBg,
    };

    switch (cardStyle) {
      case 'glassmorphism':
        return {
          ...base,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(46, 91, 255, 0.1)'}`,
          boxShadow: isDarkMode
            ? '0 16px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            : '0 12px 30px rgba(46, 91, 255, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
          '&:hover': {
            transform: 'translateY(-6px)',
            borderColor: accentColor,
            boxShadow: isDarkMode
              ? `0 20px 45px rgba(0, 0, 0, 0.5), 0 0 25px ${accentColor}33`
              : `0 16px 36px rgba(46, 91, 255, 0.14), 0 0 20px ${accentColor}25`,
          },
        };
      case 'dark-slate':
        return {
          ...base,
          border: `1px solid ${isDarkMode ? 'rgba(51, 65, 85, 0.7)' : 'rgba(203, 213, 225, 0.8)'}`,
          boxShadow: isDarkMode ? '0 10px 30px rgba(0, 0, 0, 0.5)' : '0 10px 25px rgba(0, 0, 0, 0.05)',
          '&:hover': {
            transform: 'translateY(-6px)',
            borderColor: accentColor,
            boxShadow: isDarkMode
              ? `0 18px 36px rgba(0,0,0,0.3), 0 0 20px ${accentColor}25`
              : `0 16px 32px rgba(46, 91, 255, 0.12), 0 0 18px ${accentColor}20`,
          },
        };
      case 'minimal-tech':
        return {
          ...base,
          border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
          boxShadow: isDarkMode ? 'none' : '0 2px 10px rgba(0, 0, 0, 0.03)',
          '&:hover': {
            transform: 'translateY(-4px)',
            borderColor: accentColor,
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(46, 91, 255, 0.02)',
          },
        };
      case 'cyber-glow':
      default:
        return {
          ...base,
          border: `1px solid ${isDarkMode ? 'rgba(0, 200, 150, 0.25)' : 'rgba(46, 91, 255, 0.14)'}`,
          boxShadow: isDarkMode
            ? '0 12px 35px rgba(0, 0, 0, 0.5), 0 0 15px rgba(0, 200, 150, 0.08)'
            : '0 10px 30px rgba(46, 91, 255, 0.07), 0 2px 6px rgba(0, 0, 0, 0.03), 0 0 15px rgba(0, 200, 150, 0.06)',
          '&:hover': {
            transform: 'translateY(-8px)',
            borderColor: accentColor,
            boxShadow: isDarkMode
              ? `0 20px 40px rgba(0,0,0,0.4), 0 0 28px ${accentColor}40`
              : `0 16px 36px rgba(46, 91, 255, 0.14), 0 0 24px ${accentColor}30`,
          },
        };
    }
  };

  // Visible slice for current index
  const visibleTestimonials = isCarousel
    ? activeTestimonials.slice(currentIndex, currentIndex + itemsPerView)
    : activeTestimonials;

  return (
    <Box
      id="testimonials"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: sectionBg,
        position: 'relative',
        overflow: 'hidden',
        transition: 'background-color 0.4s ease',
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background ambient pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          opacity: 0.025,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23${isDarkMode ? 'ffffff' : '000000'}' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='40' cy='40' r='1.5'/%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h2"
              component="h2"
              align="center"
              sx={{
                fontSize: { xs: '2rem', md: '2.8rem' },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                background: 'linear-gradient(135deg, #2E5BFF 0%, #00C896 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1.5,
              }}
            >
              {t('testimonialsTitle') || 'Testimonials'}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: isDarkMode ? '#94a3b8' : '#64748b',
                maxWidth: 640,
                mx: 'auto',
                fontSize: { xs: '0.95rem', md: '1.05rem' },
              }}
            >
              {t('testimonialsSubtitle') || 'Verified reviews and feedback from enterprise partners, tech leaders, and businesses we safeguard.'}
            </Typography>

            {/* Mobile Swipe Hint Badge */}
            {isCarousel && totalItems > itemsPerView && (
              <Box
                sx={{
                  display: { xs: 'inline-flex', md: 'none' },
                  alignItems: 'center',
                  gap: 0.8,
                  mt: 2,
                  px: 1.8,
                  py: 0.5,
                  borderRadius: '20px',
                  background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  fontSize: '11.5px',
                  fontWeight: 500,
                }}
              >
                <SwipeRoundedIcon sx={{ fontSize: 15, color: accentColor }} />
                <span>Swipe left or right to explore reviews</span>
              </Box>
            )}
          </motion.div>
        </Box>

        {/* Carousel Container with Touch & Mouse Swipe Handlers */}
        <Box
          sx={{
            position: 'relative',
            touchAction: 'pan-y', // Allow vertical page scrolling, capture horizontal swipes
            userSelect: 'none',
            cursor: totalItems > itemsPerView ? 'grab' : 'default',
            '&:active': { cursor: totalItems > itemsPerView ? 'grabbing' : 'default' },
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Navigation Arrows for Desktop & Tablet */}
          {isCarousel && showArrows && totalItems > itemsPerView && (
            <>
              <IconButton
                onClick={handlePrev}
                aria-label="Previous testimonial"
                sx={{
                  position: 'absolute',
                  left: { xs: -12, md: -28 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  width: { xs: 40, md: 48 },
                  height: { xs: 40, md: 48 },
                  background: isDarkMode ? 'rgba(15, 23, 42, 0.9)' : '#ffffff',
                  backdropFilter: 'blur(8px)',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(46, 91, 255, 0.14)'}`,
                  color: isDarkMode ? '#f8fafc' : '#0f172a',
                  boxShadow: isDarkMode ? '0 8px 24px rgba(0,0,0,0.25)' : '0 4px 16px rgba(46, 91, 255, 0.12)',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    background: accentColor,
                    color: '#ffffff',
                    transform: 'translateY(-50%) scale(1.08)',
                    boxShadow: `0 12px 28px ${accentColor}60`,
                  },
                }}
              >
                <ArrowBackIosNewRoundedIcon sx={{ fontSize: { xs: 16, md: 19 } }} />
              </IconButton>

              <IconButton
                onClick={handleNext}
                aria-label="Next testimonial"
                sx={{
                  position: 'absolute',
                  right: { xs: -12, md: -28 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  width: { xs: 40, md: 48 },
                  height: { xs: 40, md: 48 },
                  background: isDarkMode ? 'rgba(15, 23, 42, 0.9)' : '#ffffff',
                  backdropFilter: 'blur(8px)',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(46, 91, 255, 0.14)'}`,
                  color: isDarkMode ? '#f8fafc' : '#0f172a',
                  boxShadow: isDarkMode ? '0 8px 24px rgba(0,0,0,0.25)' : '0 4px 16px rgba(46, 91, 255, 0.12)',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    background: accentColor,
                    color: '#ffffff',
                    transform: 'translateY(-50%) scale(1.08)',
                    boxShadow: `0 12px 28px ${accentColor}60`,
                  },
                }}
              >
                <ArrowForwardIosRoundedIcon sx={{ fontSize: { xs: 16, md: 19 } }} />
              </IconButton>
            </>
          )}

          {/* Cards Grid / Carousel Viewport */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: itemsPerView === 2 ? 'repeat(2, 1fr)' : '1fr',
                md: isCarousel ? `repeat(${itemsPerView}, 1fr)` : 'repeat(auto-fit, minmax(320px, 1fr))',
              },
              gap: { xs: 2.5, md: 3.5 },
              px: { xs: 1, md: 2 },
            }}
          >
            <AnimatePresence mode="popLayout" initial={false}>
              {visibleTestimonials.map((testimonial, idx) => {
                const clientName = getLocalized(testimonial.name);
                const clientPos = getLocalized(testimonial.position);
                const clientText = getLocalized(testimonial.text);
                const uniqueKey = testimonial.id || `testi_${currentIndex}_${idx}`;

                return (
                  <motion.div
                    key={uniqueKey}
                    layout
                    initial={{ opacity: 0, x: direction * 50, scale: 0.96 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -direction * 50, scale: 0.96 }}
                    transition={{
                      duration: 0.38,
                      ease: [0.16, 1, 0.3, 1],
                      delay: idx * 0.05,
                    }}
                    style={{ height: '100%' }}
                  >
                    <Paper elevation={0} sx={getCardStyleSx()}>
                      {/* Decorative Quote Icon */}
                      {showQuoteIcon && (
                        <FormatQuoteIcon
                          sx={{
                            position: 'absolute',
                            top: 14,
                            right: 14,
                            fontSize: 46,
                            opacity: isDarkMode ? 0.08 : 0.12,
                            color: accentColor,
                            pointerEvents: 'none',
                          }}
                        />
                      )}

                      {/* Star Rating Header */}
                      {showRatings && (
                        <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Rating
                            value={Number(testimonial.rating) || 5}
                            precision={0.5}
                            readOnly
                            sx={{
                              color: '#F59E0B',
                              fontSize: '1.25rem',
                              '& .MuiRating-iconEmpty': {
                                color: isDarkMode ? '#475569' : '#cbd5e1',
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: '#F59E0B',
                              fontSize: '12px',
                              letterSpacing: '0.3px',
                            }}
                          >
                            {(Number(testimonial.rating) || 5).toFixed(1)}
                          </Typography>
                        </Box>
                      )}

                      {/* Testimonial Quote Prose */}
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 3.5,
                          flex: 1,
                          fontSize: { xs: '0.92rem', md: '0.98rem' },
                          lineHeight: 1.65,
                          color: isDarkMode ? '#e2e8f0' : '#1e293b',
                          fontStyle: 'normal',
                          position: 'relative',
                        }}
                      >
                        "{clientText}"
                      </Typography>

                      {/* Author Profile Footer */}
                      <Box sx={{ display: 'flex', alignItems: 'center', pt: 2, borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` }}>
                        <Avatar
                          sx={{
                            mr: 2,
                            width: 46,
                            height: 46,
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(46, 91, 255, 0.06)',
                            border: `2px solid ${accentColor}`,
                            fontSize: '1.4rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {testimonial.avatar || '💼'}
                        </Avatar>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              fontSize: '14.5px',
                              lineHeight: 1.3,
                              color: isDarkMode ? '#f8fafc' : '#0f172a',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {clientName}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '12.5px',
                              color: isDarkMode ? '#94a3b8' : '#64748b',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {clientPos}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </Box>
        </Box>

        {/* Carousel Bottom Controls: Pagination Dots & Counter */}
        {isCarousel && totalItems > itemsPerView && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 1.5,
              mt: { xs: 4, md: 5 },
            }}
          >
            {/* Interactive Pagination Dots */}
            {showDots && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {Array.from({ length: maxIndex + 1 }).map((_, dotIdx) => (
                  <Box
                    key={dotIdx}
                    onClick={() => {
                      setDirection(dotIdx > currentIndex ? 1 : -1);
                      setCurrentIndex(dotIdx);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Go to testimonial slide ${dotIdx + 1}`}
                    sx={{
                      width: dotIdx === currentIndex ? 28 : 8,
                      height: 8,
                      borderRadius: '4px',
                      background: dotIdx === currentIndex
                        ? accentColor
                        : (isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'),
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                      '&:hover': {
                        background: dotIdx === currentIndex
                          ? accentColor
                          : (isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)'),
                      },
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Slide Index Counter */}
            <Typography
              variant="caption"
              sx={{
                color: isDarkMode ? '#64748b' : '#94a3b8',
                fontSize: '11.5px',
                fontWeight: 600,
                letterSpacing: '0.5px',
              }}
            >
              {currentIndex + 1} of {maxIndex + 1}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Testimonials;
